/* eslint-disable max-lines */
import { Injectable, Optional } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LocalStorageService } from '@recruitee/common';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { I18nService } from '@recruitee/i18n';
import { TalentPoolsAnalytics } from '@recruitee/offers';
import { OfferKind } from '@recruitee/offers-types';
import {
  DataTableColumn,
  DataTableColumnGeometryChange,
  DataTableColumnsCache,
} from '@recruitee/user-interface';

export enum OfferColumnId {
  Title = 'title',
  CandidatesCount = 'candidatesCount',
  NewCandidatesCount = 'newCandidatesCount',
  HiredCandidatesCount = 'hiredCandidatesCount',
  DisqualifiedCount = 'disqualifiedCandidatesCount',
  QualifiedCount = 'qualifiedCandidatesCount',
  Department = 'department',
  LocationAddress = 'location',
  LocationName = 'locationName',
  State = 'stateName',
  Languages = 'languages',
  ScheduledPublish = 'publishDate',
  ScheduledClose = 'closeDate',
  PublishedAt = 'publishedAt',
  ClosedAt = 'closedAt',
  TeamMembers = 'teamMembers',
  Tags = 'offerTags',
  CreatedAt = 'createdAt',
  OpenTime = 'openTime',
  Manage = 'manage',
  Followers = 'followers',
  Eeo = 'eeo',
  Recruiter = 'recruiter',
  HiringManager = 'hiring_manager',
  JobGuid = 'job_guid',
  TalentPoolGuid = 'talent_pool_guid',
  Requisitions = 'requisitions',
  WorkModel = 'work_model',
  Priority = 'priority',
}

export type OffersTableColumn = DataTableColumn<OfferColumnId>;

@Injectable()
export class OffersTableService {
  public readonly offersTableLsKeyBase: string = 'OFFERS_TABLE_COLUMNS';
  public readonly defaultColumns: OffersTableColumn[] = [
    {
      id: OfferColumnId.Title,
      label: this.i18n.translate('app.offers.table.title'),
      visible: true,
      geometry: {
        width: 300,
        minWidth: 200,
        autoFitProp: 'width',
      },
      options: {
        draggable: true,
        resizable: true,
        removable: false,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.JobGuid,
      label: this.i18n.translate('app.offers.table.job_id'),
      visible: false,
      geometry: {
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: false,
      },
    },
    {
      id: OfferColumnId.TalentPoolGuid,
      label: this.i18n.translate('app.offers.table.talent_pool_id'),
      visible: false,
      geometry: {
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: false,
      },
    },
    {
      id: OfferColumnId.Eeo,
      label: this.i18n.translate('app.offers.table.eeo_compliance'),
      visible: false,
      geometry: {
        width: 120,
        minWidth: 120,
        maxWidth: 140,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.CandidatesCount,
      label: this.i18n.translate('app.offers.table.candidates_count'),
      visible: true,
      geometry: {
        width: 120,
        minWidth: 120,
        maxWidth: 140,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.NewCandidatesCount,
      label: this.i18n.translate('app.offers.table.new_candidates_count'),
      visible: false,
      geometry: {
        width: 130,
        minWidth: 130,
        maxWidth: 130,
      },
      options: {
        draggable: false,
        resizable: false,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.WorkModel,
      label: this.i18n.translate('app.offers.table.work_model'),
      visible: true,
      geometry: {
        width: 180,
        minWidth: 180,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.Requisitions,
      label: this.i18n.translate('app.offers.table.requisitions'),
      visible: true,
      geometry: {
        width: 300,
        minWidth: 200,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.HiredCandidatesCount,
      label: this.i18n.translate('app.offers.table.hired_candidates_count'),
      visible: true,
      geometry: {
        width: 130,
        minWidth: 130,
        maxWidth: 130,
      },
      options: {
        draggable: false,
        resizable: false,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.Department,
      label: this.i18n.translate('app.offers.table.department'),
      visible: true,
      geometry: {
        width: 110,
        minWidth: 110,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.LocationAddress,
      label: this.i18n.translate('app.offers.table.location'),
      visible: true,
      geometry: {
        width: 200,
        minWidth: 100,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.State,
      label: this.i18n.translate('app.offers.table.state'),
      visible: true,
      geometry: {
        width: 200,
        minWidth: 100,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.Languages,
      label: this.i18n.translate('app.offers.table.languages'),
      visible: true,
      geometry: {
        width: 155,
        minWidth: 155,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: false,
      },
    },
    {
      id: OfferColumnId.ScheduledPublish,
      label: this.i18n.translate('app.offers.table.scheduled_publish'),
      visible: true,
      geometry: {
        width: 180,
        minWidth: 180,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: false,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.ScheduledClose,
      label: this.i18n.translate('app.offers.table.scheduled_close'),
      visible: true,
      geometry: {
        width: 180,
        minWidth: 180,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: false,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.Tags,
      label: this.i18n.translate('app.offers.table.tags'),
      visible: true,
      geometry: {
        width: 200,
        minWidth: 100,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: false,
      },
    },
    {
      id: OfferColumnId.QualifiedCount,
      label: this.i18n.translate('app.offers.table.qualified'),
      visible: false,
      geometry: {
        width: 120,
        minWidth: 120,
        maxWidth: 140,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.DisqualifiedCount,
      label: this.i18n.translate('app.offers.table.disqualified'),
      visible: false,
      geometry: {
        width: 120,
        minWidth: 120,
        maxWidth: 140,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.Followers,
      label: this.i18n.translate('app.offers.table.followers'),
      visible: false,
      geometry: {
        width: 120,
        minWidth: 120,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: false,
      },
    },
    // {
    //   id: OfferColumnId.TeamMembers,
    //   label: this.i18n.translate('app.offers.table.team_members'),
    //   visible: true,
    //   geometry: {
    //     width: 120,
    //     minWidth: 120,
    //     maxWidth: 120,
    //     autoFitProp: 'width',
    //   },
    //   options: {
    //     draggable: false,
    //     resizable: false,
    //     removable: true,
    //     sortable: false,
    //   },
    // },
    {
      id: OfferColumnId.CreatedAt,
      label: this.i18n.translate('app.offers.table.create_date'),
      visible: false,
      geometry: {
        width: 130,
        minWidth: 130,
        maxWidth: 150,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.PublishedAt,
      label: this.i18n.translate('app.offers.table.publish_date'),
      visible: false,
      geometry: {
        width: 130,
        minWidth: 130,
        maxWidth: 190,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.ClosedAt,
      label: this.i18n.translate('app.offers.table.close_date'),
      visible: false,
      geometry: {
        width: 130,
        minWidth: 130,
        maxWidth: 190,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.Recruiter,
      label: this.i18n.translate('app.offers.table.recruiter'),
      visible: false,
      geometry: {
        width: 160,
        minWidth: 120,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.HiringManager,
      label: this.i18n.translate('app.offers.table.hiring_manager'),
      visible: false,
      geometry: {
        width: 160,
        minWidth: 120,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: true,
        removable: true,
        sortable: true,
      },
    },
    {
      id: OfferColumnId.Manage,
      label: this.i18n.translate('app.offers.table.manage'),
      visible: true,
      geometry: {
        width: 275,
        minWidth: 275,
        maxWidth: 275,
        autoFitProp: 'width',
      },
      options: {
        draggable: false,
        resizable: false,
        removable: false,
        sortable: false,
      },
    },
  ];

  public tableSubject$: BehaviorSubject<{
    allColumns: OffersTableColumn[];
    visibleColumns: OffersTableColumn[];
    autoFit: boolean;
  }>;

  public table$: Observable<{
    allColumns: OffersTableColumn[];
    visibleColumns: OffersTableColumn[];
    autoFit: boolean;
  }>;

  public get table(): {
    allColumns: OffersTableColumn[];
    visibleColumns: OffersTableColumn[];
    autoFit: boolean;
  } {
    return this.tableSubject$.getValue();
  }

  private scope: OfferKind;
  private eeoEnabled: boolean;
  private offersTableLsKey: string;
  private requisitionsEnabled: boolean;
  private ngOnDestroy$ = new Subject<void>();
  private hasNewTalentPoolsFeature = injectDevFlag(DevFlag.NewTalentPools);

  constructor(
    private i18n: I18nService,
    private localStorage: LocalStorageService,
    @Optional() private talentPoolsAnalytics: TalentPoolsAnalytics,
  ) {}

  public initialize(
    scope: OfferKind,
    featuresEnabled: {
      eeo$: Observable<boolean>;
      requisitions$: Observable<boolean>;
    },
  ): void {
    this.scope = scope;
    combineLatest([featuresEnabled.eeo$, featuresEnabled.requisitions$])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([eeoEnabled, requisitionsEnabled]) => {
        this.eeoEnabled = eeoEnabled;
        this.requisitionsEnabled = requisitionsEnabled;
        this.calculateVisibleColumns();
      });
  }

  public changeTableOptions(options: {
    autoFit?: boolean;
    columns?: OffersTableColumn[];
    track?: boolean;
  }): void {
    const allColumns = options.columns ? options.columns : this.table.allColumns;
    const visibleColumns = allColumns.filter(c => c.visible);

    const autoFit = options.autoFit !== undefined ? options.autoFit : this.table.autoFit;

    if (options.columns && options.track) this.trackAdjustedColumn(visibleColumns);
    this.localStorage.set(this.offersTableLsKey, {
      autoFit,
      columns: this.toCacheableMap(allColumns, visibleColumns),
    });

    this.tableSubject$.next({
      ...this.table,
      allColumns,
      visibleColumns,
      autoFit,
    });
  }

  public handleGeometryChange(event: DataTableColumnGeometryChange): void {
    const allColumns = this.table.allColumns.map(col => {
      const modified = event.columns.find(c => c.id === col.id);
      return {
        ...col,
        geometry: modified ? modified.geometry : col.geometry,
      };
    });
    this.changeTableOptions({ columns: allColumns, autoFit: event.autofit });
  }

  public toggleColumnSort({ id, sort }: DataTableColumn): void {
    const nextSort: 'asc' | 'desc' = sort === 'desc' ? 'asc' : 'desc';

    this.changeTableOptions({
      columns: this.table.allColumns.map(column =>
        column.id === id
          ? {
              ...column,
              sort: nextSort,
            }
          : {
              ...column,
              sort: undefined,
            },
      ),
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  // eslint-disable-next-line max-lines-per-function
  private calculateVisibleColumns(): void {
    this.offersTableLsKey = `${this.offersTableLsKeyBase}_${this.scope}`;

    const defaultScopedColumns =
      this.scope === OfferKind.TalentPool
        ? this.defaultColumns.filter(
            c =>
              c.id !== OfferColumnId.State &&
              c.id !== OfferColumnId.Eeo &&
              c.id !== OfferColumnId.Tags &&
              c.id !== OfferColumnId.ScheduledPublish &&
              c.id !== OfferColumnId.ScheduledClose &&
              c.id !== OfferColumnId.PublishedAt &&
              c.id !== OfferColumnId.ClosedAt &&
              c.id !== OfferColumnId.HiringManager &&
              c.id !== OfferColumnId.Recruiter &&
              c.id !== OfferColumnId.JobGuid &&
              c.id !== OfferColumnId.Requisitions &&
              c.id !== OfferColumnId.WorkModel &&
              c.id !== OfferColumnId.HiredCandidatesCount &&
              c.id !== OfferColumnId.LocationAddress &&
              (this.hasNewTalentPoolsFeature
                ? c.id !== OfferColumnId.QualifiedCount && c.id !== OfferColumnId.DisqualifiedCount
                : true),
          )
        : this.defaultColumns.filter(column => {
            switch (column.id) {
              case OfferColumnId.Eeo:
                return this.eeoEnabled;
              case OfferColumnId.TalentPoolGuid:
                return false;
              case OfferColumnId.Requisitions:
                return this.requisitionsEnabled;
              default:
                return true;
            }
          });

    const cachedTableOptions: {
      autoFit: boolean;
      columns: DataTableColumnsCache;
    } = this.localStorage.get(this.offersTableLsKey, {
      autoFit: true,
      columns: this.toCacheableMap(defaultScopedColumns),
    });

    const allColumns = defaultScopedColumns
      .map(column => {
        const cachedColumn = cachedTableOptions.columns[column.id];
        return cachedColumn
          ? {
              ...column,
              visible: cachedColumn.visible,
              index: cachedColumn.index,
            }
          : column;
      })
      .sort(
        (a: DataTableColumn<OfferColumnId> | any, b: DataTableColumn<OfferColumnId> | any) =>
          a.index - b.index,
      );

    const visibleColumns = allColumns.filter(column => column.visible);

    this.tableSubject$ = new BehaviorSubject<{
      allColumns: OffersTableColumn[];
      visibleColumns: OffersTableColumn[];
      autoFit: boolean;
    }>({
      allColumns,
      visibleColumns,
      autoFit: cachedTableOptions.autoFit,
    });

    this.table$ = this.tableSubject$.asObservable();
  }

  private toCacheableMap(
    columns: OffersTableColumn[],
    visibleColumns?: OffersTableColumn[],
  ): DataTableColumnsCache {
    return columns.reduce(
      (agg, c, index) => ({
        [c.id]: {
          index,
          visible: visibleColumns ? visibleColumns.includes(c) : c.visible,
        },
        ...agg,
      }),
      {},
    );
  }

  private trackAdjustedColumn(visibleColumns: OffersTableColumn[]) {
    const addedColumn = visibleColumns.find(
      column => !this.tableSubject$.value.visibleColumns.find(c => c.id === column.id),
    );
    const removedColumn = this.tableSubject$.value.visibleColumns.find(
      column => !visibleColumns.find(c => c.id === column.id),
    );
    const selectedFiled = (addedColumn?.id || removedColumn?.id)!;
    const enabled = !!addedColumn;

    this.talentPoolsAnalytics.trackAdjustColumns(selectedFiled, enabled);
  }
}
